import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="33orla998ybt7">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="swqchiblnjxz">
      <BrowserRouter data-id="element-2" data-mp="36huv2f1ajz4">
        <ScrollToTop data-id="element-3" data-mp="35ymodf6r9ya" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-4" data-mp="356t1855fman">
          <SkipLink data-id="element-5" data-mp="3ubzrw5cgz9c0" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-6" data-mp="83i2vivg1wsv" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-7" data-mp="3gj1sm7wwox36">
            <Header data-id="element-8" data-mp="3bn314bga6yyj" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-9" data-mp="3bo4lj6j6sxsx">
            <Routes data-id="element-10" data-mp="3lkr41oo1jcrs">
              <Route path="/" element={<Home data-id="element-12" data-mp="3liwkoesf3spt" />} data-id="element-11" data-mp="3lk7q0j1ih8j1" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-14" data-mp="2xud8ho68yvsg" />} data-id="element-13" data-mp="3limryjfzn7gf" />
              <Route path="/pricing" element={<PricingPage data-id="element-16" data-mp="2xsk3edba8jmk" />} data-id="element-15" data-mp="2xtc0xtofwrfh" />
              <Route path="/faq" element={<FAQPage data-id="element-18" data-mp="2sv0mf5n79sqx" />} data-id="element-17" data-mp="2swlkh58q3ttj" />
              <Route path="/contact" element={<Contact data-id="element-20" data-mp="18d9ds8p0zot8" />} data-id="element-19" data-mp="2stpj16xo2n0c" />
            </Routes>
          </main>
          <Footer data-id="element-21" data-mp="18bojmk6mi7vw" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-22" data-mp="1d62gys4uip37" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}